import type { ProductAttrComputer } from '@athena-lib/ProductWizard/product/helper/ProductAttrComputer';
import { productWizardConsole } from '@athena-lib/ProductWizard/util/productWizardLog';
import type { ProductProcessorInterface } from '../../product/processor/ProductProcessorInterface';
import type { ProductItem } from '../../product/model/ProductItem';
import type { SizeHelper } from '../../helper/SizeHelper';

export class SizeProcessor implements ProductProcessorInterface {
  protected lastEvaluation: string = '';
  public name: string = SizeProcessor.name;

  constructor(protected sizeHelper: SizeHelper) {}

  invoke(productItem: ProductItem, attrComputer: ProductAttrComputer): void {
    this.sizeHelper.evaluate();
    const evaluation: string = this.sizeHelper.toString();

    // Do not run if nothing has changed regarding size settings
    if (this.lastEvaluation != evaluation) {
      this.lastEvaluation = evaluation;

      productWizardConsole.log(
        'SizeHelper try auto fit',
        this.sizeHelper.getMeasureAttrInMM('width_mm'),
        this.sizeHelper.getMeasureAttrInMM('height_mm'),
      );
      let widthMM = this.sizeHelper.getMeasureAttrInMM('width_mm');
      let heightMM = this.sizeHelper.getMeasureAttrInMM('height_mm');

      if (this.sizeHelper.fixedSize) {
        // Auto fit to size constraints
        const sizeOptions: string[] = attrComputer.getSuggestedValues('size') as string[];
        const {
          currentDimensionsFoundInSizeOptions,
          defaultSizeOption,
          defaultMeassures,
          convertedMeasures,
        } = this.iterateSizeOption(sizeOptions, widthMM, heightMM);

        if (
          !currentDimensionsFoundInSizeOptions &&
          this.isDefault(defaultMeassures, defaultSizeOption)
        ) {
          const closest = this.findClosestMatch(convertedMeasures, widthMM, heightMM);

          // The discrepency with Epoxy (3") is 1.26
          if (closest?.distance && closest.distance < 1.5) {
            productItem.setAttrValue('size', closest.index);
            productWizardConsole.log('Size difference is within 1.5 mm, not adjusting size.');
          } else {
            productWizardConsole.log('Auto fit to fixed meassures', 'from', widthMM, heightMM);

            productItem.setAttrValue('size', defaultSizeOption);
            widthMM = this.sizeHelper.setMeasureAttr('width_mm', defaultMeassures?.width ?? 50); // Default to 50mm?
            heightMM = this.sizeHelper.setMeasureAttr('height_mm', defaultMeassures?.height ?? 50); // Default to 50mm?

            productWizardConsole.log('...to', widthMM, heightMM, defaultMeassures);
          }
        }
      }

      const maxSide = Math.max(widthMM, heightMM);
      const minSide = Math.min(widthMM, heightMM);
      this.setMeasures(minSide, maxSide, widthMM, heightMM);
    }
  }

  private setMeasures(minSide: number, maxSide: number, widthMM: number, heightMM: number) {
    if (
      this.sizeHelper.hasMaxSizeOtherSide() &&
      [maxSide, minSide].every((side) => side > this.sizeHelper.maxSizeOtherSideMM)
    ) {
      productWizardConsole.log(
        'Auto fit because larger than max size other side',
        this.sizeHelper.maxSizeOtherSideMM,
      );

      const ratio = minSide / this.sizeHelper.maxSizeOtherSideMM;
      this.sizeHelper.setMeasureAttrInMM('width_mm', widthMM / ratio);
      this.sizeHelper.setMeasureAttrInMM('height_mm', heightMM / ratio);
    } else if (maxSide > this.sizeHelper.maxSizeMM) {
      productWizardConsole.log('Auto fit because larger than max size', this.sizeHelper.maxSizeMM);

      const ratio = maxSide / this.sizeHelper.maxSizeMM;
      this.sizeHelper.setMeasureAttrInMM('width_mm', widthMM / ratio);
      this.sizeHelper.setMeasureAttrInMM('height_mm', heightMM / ratio);
    } else if (minSide < this.sizeHelper.minSizeMM) {
      productWizardConsole.log('Auto fit because smaller than min size', this.sizeHelper.minSizeMM);

      const ratio = this.sizeHelper.minSizeMM / minSide;
      this.sizeHelper.setMeasureAttrInMM('width_mm', widthMM * ratio);
      this.sizeHelper.setMeasureAttrInMM('height_mm', heightMM * ratio);
    }
  }

  private isDefault(
    defaultMeassures: { width: number; height: number } | null,
    defaultSizeOption: string | null,
  ): boolean {
    return (defaultMeassures && Boolean(defaultSizeOption)) ?? false;
  }

  private iterateSizeOption(sizeOptions: string[], widthMM: number, heightMM: number) {
    let currentDimensionsFoundInSizeOptions: boolean = false;
    let defaultSizeOption: string | null = null;
    let defaultMeassures = null;
    const convertedMeasures: number[][] = [];
    for (const sizeOptionIndex in sizeOptions) {
      const sizeOption = sizeOptions[sizeOptionIndex];
      const meassures = this.sizeHelper.extractMeassuresFromString(sizeOption);
      if (!defaultMeassures) {
        defaultMeassures = meassures;
        defaultSizeOption = sizeOption;
      }
      const widthOptionInMM = this.sizeHelper.convertMeassureToMM(meassures.width);
      const heightOptionInMM = this.sizeHelper.convertMeassureToMM(meassures.height);

      convertedMeasures[sizeOptionIndex] = [widthOptionInMM, heightOptionInMM];

      if (widthMM == widthOptionInMM && heightOptionInMM == heightMM) {
        currentDimensionsFoundInSizeOptions = true;
        return {
          currentDimensionsFoundInSizeOptions,
          defaultSizeOption,
          defaultMeassures,
          convertedMeasures,
        };
      }
    }
    return {
      currentDimensionsFoundInSizeOptions,
      defaultSizeOption,
      defaultMeassures,
      convertedMeasures,
    };
  }

  //TODO: This is purely to find the closest match for Epoxy 3" x 1.5", which gets converted to 76mm x 38mm,
  //instead of 77mm x 38mm. It calculated the difference (distance) which in this case is 1.
  //Then the set filter (of 76mm) is ignored and not enforced. This should probably be done the other way around.
  findClosestMatch(
    arrayOfArrays: number[][],
    inputNumber1: number,
    inputNumber2: number,
  ): ClosestMatchResult | null {
    let closestMatch: ClosestMatchResult | null = null;
    let minDistance = Number.MAX_SAFE_INTEGER;

    for (let i = 0; i < arrayOfArrays.length; i++) {
      const distance =
        Math.abs(inputNumber1 - arrayOfArrays[i][0]) + Math.abs(inputNumber2 - arrayOfArrays[i][1]);

      if (distance < minDistance) {
        minDistance = distance;
        closestMatch = { index: i, distance };
      }
    }

    return closestMatch;
  }
}

interface ClosestMatchResult {
  index: number;
  distance: number;
}
